/**
 * 픽업 시간 관리 관련 함수
 * 기존 order.ts에서 분리
 */

import { supabase } from '@/lib/supabase';

/**
 * 픽업 시간 업데이트
 */
export async function updatePickupTime(
  orderId: string,
  pickupTime: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('orders')
      .update({ 
        pickup_time: pickupTime,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);

    if (error) {
      return { success: false, error: '픽업 시간 업데이트에 실패했습니다.' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating pickup time:', error);
    return { success: false, error: '픽업 시간 업데이트 중 오류가 발생했습니다.' };
  }
}

/**
 * 예상 픽업 시간 계산
 */
export function calculateExpectedPickupTime(
  orderType: string,
  restaurantPreparationTime: number = 20
): Date {
  const now = new Date();
  
  switch (orderType) {
    case 'DEADLINE_DISCOUNT':
      // 마감할인: 주문 즉시 픽업 가능
      return now;
      
    case 'RESERVATION_DISCOUNT':
      // 예약할인: 다음날 영업시작 1시간 후
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(11, 0, 0, 0); // 11 AM next day
      return tomorrow;
      
    case 'REGULAR_ORDER':
    default:
      // 일반주문: 준비시간 후 픽업
      const pickupTime = new Date(now);
      pickupTime.setMinutes(pickupTime.getMinutes() + restaurantPreparationTime);
      return pickupTime;
  }
}

/**
 * 픽업 가능 시간대 조회
 */
export async function getAvailablePickupTimes(
  restaurantId: string,
  date: Date
): Promise<string[]> {
  try {
    // Get restaurant operating hours
    const { data: restaurant } = await supabase
      .from('restaurants')
      .select('operating_hours')
      .eq('id', restaurantId)
      .single();

    if (!restaurant || !restaurant.operating_hours) {
      return [];
    }

    const operatingHours = restaurant.operating_hours;
    const availableTimes: string[] = [];
    
    // Parse operating hours and generate 30-minute slots
    const dayOfWeek = date.getDay();
    const todayHours = operatingHours[dayOfWeek];
    
    if (todayHours && todayHours.open && todayHours.close) {
      const openTime = new Date(date);
      const [openHour, openMinute] = todayHours.open.split(':').map(Number);
      openTime.setHours(openHour, openMinute, 0, 0);
      
      const closeTime = new Date(date);
      const [closeHour, closeMinute] = todayHours.close.split(':').map(Number);
      closeTime.setHours(closeHour, closeMinute, 0, 0);
      
      // Add 1 hour buffer from opening time for reservation discount
      const startTime = new Date(openTime);
      startTime.setHours(startTime.getHours() + 1);
      
      // Generate 30-minute slots
      const current = new Date(startTime);
      while (current < closeTime) {
        availableTimes.push(current.toISOString());
        current.setMinutes(current.getMinutes() + 30);
      }
    }
    
    return availableTimes;
  } catch (error) {
    console.error('Error getting available pickup times:', error);
    return [];
  }
}

/**
 * 픽업 시간 유효성 검사
 */
export function validatePickupTime(
  pickupTime: Date,
  orderType: string
): { valid: boolean; error?: string } {
  const now = new Date();
  
  switch (orderType) {
    case 'DEADLINE_DISCOUNT':
      // 마감할인: 당일 픽업만 가능
      if (pickupTime.toDateString() !== now.toDateString()) {
        return { valid: false, error: '마감할인은 당일 픽업만 가능합니다.' };
      }
      return { valid: true };
      
    case 'RESERVATION_DISCOUNT':
      // 예약할인: 다음날 픽업만 가능
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (pickupTime.toDateString() !== tomorrow.toDateString()) {
        return { valid: false, error: '예약할인은 다음날 픽업만 가능합니다.' };
      }
      // 픽업 시간이 영업시작 1시간 후부터인지 확인
      if (pickupTime.getHours() < 11) {
        return { valid: false, error: '예약할인 픽업은 오전 11시 이후부터 가능합니다.' };
      }
      return { valid: true };
      
    case 'REGULAR_ORDER':
    default:
      // 일반주문: 현재 시간 이후면 가능
      if (pickupTime < now) {
        return { valid: false, error: '과거 시간으로는 픽업 시간을 설정할 수 없습니다.' };
      }
      return { valid: true };
  }
}

/**
 * 주문 타입별 픽업 종료 시간 계산
 */
export async function getPickupEndTime(orderId: string): Promise<{ success: boolean; endTime?: string; error?: string }> {
  try {
    // getOrderById를 import해야 하므로 supabase를 직접 사용
    const { data: order, error } = await supabase
      .from('orders')
      .select(`
        *,
        restaurant:restaurants(*)
      `)
      .eq('id', orderId)
      .single();
    
    if (error || !order) {
      return { success: false, error: '주문을 찾을 수 없습니다.' };
    }

    if (order.status !== 'READY' || !order.pickup_time) {
      return { success: false, error: '픽업 시간이 설정되지 않았습니다.' };
    }

    const pickupStartTime = new Date(order.pickup_time);
    let pickupEndTime: Date;

    switch (order.order_type) {
      case 'DEADLINE_DISCOUNT':
        // 마감할인: 설정된 마감할인 종료시간 또는 영업종료시간까지
        const restaurantData = order.restaurant as any;
        if (restaurantData && restaurantData.deadline_discount_end_time) {
          // 오늘 날짜에 설정된 종료시간 적용
          const today = new Date(pickupStartTime);
          const [endHours, endMinutes] = restaurantData.deadline_discount_end_time.split(':').map(Number);
          pickupEndTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), endHours, endMinutes);
        } else {
          // 기본값: 영업종료시간 (영업종료 2시간 전이 기본 마감할인 시간이므로 영업종료까지)
          const businessHours = restaurantData?.business_hours;
          if (businessHours) {
            const today = pickupStartTime.getDay();
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const todayData = businessHours[dayNames[today]];
            
            if (todayData?.close_time) {
              const [closeHours, closeMinutes] = todayData.close_time.split(':').map(Number);
              pickupEndTime = new Date(pickupStartTime.getFullYear(), pickupStartTime.getMonth(), pickupStartTime.getDate(), closeHours, closeMinutes);
            } else {
              // 영업시간 정보가 없으면 픽업시작 + 2시간
              pickupEndTime = new Date(pickupStartTime.getTime() + (2 * 60 * 60 * 1000));
            }
          } else {
            // 영업시간 정보가 없으면 픽업시작 + 2시간
            pickupEndTime = new Date(pickupStartTime.getTime() + (2 * 60 * 60 * 1000));
          }
        }
        break;

      case 'REGULAR_ORDER':
        // 일반주문: 픽업 시작 + 2시간
        pickupEndTime = new Date(pickupStartTime.getTime() + (2 * 60 * 60 * 1000));
        break;

      case 'RESERVATION_DISCOUNT':
        // 예약할인: 선택한 픽업 시간 + 1시간 (여유 시간)
        pickupEndTime = new Date(pickupStartTime.getTime() + (1 * 60 * 60 * 1000));
        break;

      default:
        return { success: false, error: '알 수 없는 주문 유형입니다.' };
    }

    return { success: true, endTime: pickupEndTime.toISOString() };
  } catch (error) {
    console.error('Error calculating pickup end time:', error);
    return { success: false, error: '픽업 종료 시간 계산 중 오류가 발생했습니다.' };
  }
}

/**
 * 픽업 시간 설명 생성
 */
export function getPickupTimeDescription(
  orderType: string,
  status: string,
  pickupTime?: string | null,
  readyAt?: string | null
): string {
  switch (orderType) {
    case 'DEADLINE_DISCOUNT':
      if (status === 'READY' && readyAt) {
        const readyTime = new Date(readyAt);
        const endTime = new Date(readyTime);
        endTime.setMinutes(endTime.getMinutes() + 60);
        
        const formatTime = (date: Date) => {
          const hours = date.getHours();
          const minutes = date.getMinutes();
          const period = hours >= 12 ? '오후' : '오전';
          const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
          return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
        };
        
        return `${formatTime(readyTime)} - ${formatTime(endTime)}`;
      }
      return '준비 완료 후 60분 이내';
      
    case 'RESERVATION_DISCOUNT':
      if (pickupTime) {
        const time = new Date(pickupTime);
        const hours = time.getHours();
        const minutes = time.getMinutes();
        const period = hours >= 12 ? '오후' : '오전';
        const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
        return `${period} ${displayHours}:${minutes.toString().padStart(2, '0')}`;
      }
      return '익일 수령';
      
    case 'REGULAR_ORDER':
    default:
      if (status === 'READY') {
        return '준비 완료 - 픽업 가능';
      } else if (status === 'ACCEPTED') {
        return '준비 중';
      }
      return '주문 즉시';
  }
}